import React, { useState, } from "react";
import { getIn, } from "formik";
import {
    Box,
    Button,
    Typography,
    LinearProgress,
    IconButton,
    FormControl,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { Icon } from "@iconify/react/dist/iconify.js";
import CustomDivider from "../../utils/CustomDivider";
import { getFileUrl } from "../../utils/getFileUrl";



export const DropzoneUploadFormField = ({
    field,
    form,
    label,
}) => {
    const errorText =
        getIn(form.touched, field.name) && getIn(form.errors, field.name);

    const [file, setFile] = useState(field.value || null);
    const [progress, setProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);


    const fileSizeMB = file ? file.size / (1024 * 1024) : 0;
    const isTooLarge = fileSizeMB > 2;

    // File validation
    const validateFile = (file) => {
        const maxSize = 2 * 1024 * 1024;
        const allowedTypes = ["image/jpeg", "image/png", "application/pdf", "video/mp4"];
        if (!allowedTypes?.includes?.(file.type)) {
            return "Only JPEG, PNG, JPG  files are allowed.";
        }
        if (file.size > maxSize) {
            return "File size should be less than 50MB.";
        }
        return null;
    };

    const simulateUpload = (selectedFile) => {
        setIsUploading(true);
        setProgress(0);
        const interval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setIsUploading(false);
                    form.setFieldValue(field.name, selectedFile);
                    return 100;
                }
                return prev + 10;
            });
        }, 200);
    };

    const handleFileSelect = (event) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;
        const validationError = validateFile(selectedFile);
        if (validationError) {
            form.setFieldError(field.name, validationError);
            return;
        }

        setFile(selectedFile);
        simulateUpload(selectedFile);
    };

    const onRemove = () => {
        setFile(null);
        setProgress(0);
        form.setFieldValue(field.name, "");
    };

    const renderFileIcon = () => {
        if (!file) return null;

        // If file is a string (URL from backend)
        if (typeof file === "string") {
            // Ensure the URL is absolute
            const url = file.startsWith("http") ? file : `${file.startsWith("/") ? "" : "/"}${file}`;
            const name = file.split("/").pop(); // extract file name
            return (
                <img
                    src={getFileUrl(url)} // no encodeURI here if URL already works in browser
                    alt={name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
            );
        }

        // If file is a newly uploaded File object
        if (file.type?.includes("image")) {
            return (
                <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
            );
        }

        if (file.type === "application/pdf") {
            return <CloudUploadIcon sx={{ fontSize: 30, color: "red" }} />;
        }

        if (file.type === "video/mp4") {
            return <CloudUploadIcon sx={{ fontSize: 30, color: "purple" }} />;
        }

        return <CloudUploadIcon sx={{ fontSize: 30, color: "#808080" }} />;
    };

    return (
        <FormControl fullWidth>
            {!file ? (
                <Box
                    sx={{
                        border: "2px dashed #ccc",
                        borderRadius: 2,
                        p: 4,
                        textAlign: "center",
                        "&:hover": { borderColor: "#888" },
                    }}
                >
                    <Icon icon="streamline-ultimate:cloud-add" width="35" height="35" />
                    <CustomDivider size={10} />
                    <Typography variant="body1">
                        {label || "Choose a file or drag & drop it here"}
                    </Typography>
                    <CustomDivider size={5} />
                    <Typography variant="body2" color="grey.100">
                        JPEG, PNG, JPG  (max 2MB)
                    </Typography>
                    <CustomDivider size={20} />
                    <Button
                        variant="outlined"
                        component="label"
                        style={{ border: "2px solid #646464", borderRadius: "2rem" }}
                    >
                        <Typography color="#646464"> Browse Files</Typography>
                        <input
                            type="file"
                            hidden
                            onChange={handleFileSelect}
                            accept=".jpeg,.jpg,.png,.pdf,.mp4"
                        />
                    </Button>
                </Box>
            ) : (
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "#f7f9fc",
                        position: "relative",
                    }}
                >
                    {/* File Info */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 5, flex: 1, pt: 2 }}>
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: "6px",
                                overflow: "hidden",
                            }}
                        >
                            {renderFileIcon()}
                        </Box>

                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: "break-all" }}>
                                {typeof file === "string" ? file.split("/").pop() : file?.name}
                            </Typography>
                            <Typography
                                variant="body2"
                                color={isTooLarge ? "error.main" : "text.primary"}
                                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                            >
                                {typeof fileSizeMB === "number" && !isNaN(fileSizeMB) && `${fileSizeMB.toFixed(2)} MB • `}

                                <Box
                                    component="span"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        animation: isUploading ? "spin 1s linear infinite" : "none",
                                        "@keyframes spin": {
                                            "0%": { transform: "rotate(0deg)" },
                                            "100%": { transform: "rotate(360deg)" },
                                        },
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="#375EF9"
                                    >
                                        <path d="M12 6.5A1.5 1.5 0 0 0 13.5 5V2a1.5 1.5 0 0 0-3 0v3A1.5 1.5 0 0 0 12 6.5m-3.73-.39L6.15 4A1.53 1.53 0 0 0 4 4a1.48 1.48 0 0 0-.44 1.06A1.52 1.52 0 0 0 4 6.11l2.15 2.12a1.5 1.5 0 0 0 1.06.44a1.5 1.5 0 0 0 1.06-2.56M.5 12A1.5 1.5 0 0 0 2 13.5h3a1.5 1.5 0 0 0 0-3H2A1.5 1.5 0 0 0 .5 12M7 15.52a1.48 1.48 0 0 0-1.1.48l-2.13 2.08a1.51 1.51 0 0 0 0 2.12a1.51 1.51 0 0 0 2.13 0L8 18.08a1.5 1.5 0 0 0-1-2.56M10.5 22a1.5 1.5 0 0 0 3 0v-3a1.5 1.5 0 0 0-3 0Zm5.55-6a1.5 1.5 0 0 0 0 2.12l2.12 2.12a1.52 1.52 0 0 0 1.06.44a1.54 1.54 0 0 0 1.07-.44a1.5 1.5 0 0 0 .43-1.06a1.48 1.48 0 0 0-.44-1.07L18.17 16a1.5 1.5 0 0 0-2.12 0m7.45-4a1.5 1.5 0 0 0-1.5-1.5h-3a1.5 1.5 0 0 0-1.5 1.5a1.5 1.5 0 0 0 1.5 1.5h3a1.5 1.5 0 0 0 1.5-1.5M19 3.41a1.52 1.52 0 0 0-1 .44L15.86 6a1.5 1.5 0 0 0 0 2.12a1.51 1.51 0 0 0 2.13 0L20.11 6A1.5 1.5 0 0 0 19 3.41" />
                                    </svg>
                                </Box>
                                {isTooLarge
                                    ? "File too large (max 50 MB)"
                                    : isUploading
                                        ? "Uploading..."
                                        : "Completed"}
                            </Typography>
                        </Box>
                    </Box>
                    <CustomDivider size={10} />
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        color="primary"
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            mt: 0.5,
                            width: "100%",
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: "#375EF9",
                            },
                        }}
                    />

                    <IconButton
                        onClick={onRemove}
                        size="small"
                        sx={{
                            position: "absolute",
                            top: 7,
                            right: 8,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "2px solid #292D32",
                            p: 1.1,
                        }}
                    >
                        <CloseIcon sx={{ width: 18, height: 18, color: "#292D32" }} />
                    </IconButton>
                </Box>
            )}

            {errorText && (
                <Typography variant="body2" color="error.main" sx={{ mt: 0.5, display: "block" }}>
                    {errorText}
                </Typography>
            )}
        </FormControl>
    );
};
